import { API_BASE_URL } from "../apiConfig";
import React, { useEffect, useMemo, useState } from "react";
import {
  FaChartBar,
  FaRedo,
  FaFilePdf,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaUser,
  FaFileAlt,
  FaExclamationTriangle,
  FaUsers,
  FaClipboardList,
  FaCheckSquare,
  FaRegClock,
  FaLightbulb,
  FaTruck,
} from "react-icons/fa";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "./GenerateAnalytics.css";

const API_URL = `${API_BASE_URL}/api/complaint/analytics-report`;

const defaultFilters = {
  startDate: "",
  endDate: "",
  timePeriod: "all",
  area: "all",
  driver: "all",
  reportType: "summary",
};

const insightOptions = [
  { key: "risk", label: "High Risk Areas", icon: <FaExclamationTriangle /> },
  { key: "overview", label: "Complaints Overview", icon: <FaClipboardList /> },
  { key: "users", label: "User Registrations", icon: <FaUsers /> },
  { key: "drivers", label: "Driver Performance", icon: <FaUser /> },
  { key: "waste", label: "Waste Types Breakdown", icon: <FaChartBar /> },
];

const GenerateAnalytics = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedInsights, setSelectedInsights] = useState(["risk", "overview", "users", "drivers"]);
  const [report, setReport] = useState(null);
  const [options, setOptions] = useState({ areas: [], drivers: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}?reportType=options`)
      .then((res) => res.json())
      .then((data) => {
        setOptions({
          areas: data.areas || [],
          drivers: data.drivers || [],
        });
      })
      .catch((err) => console.error("Failed to load analytics options:", err));
  }, []);

  const dateLabel = useMemo(() => {
    if (!filters.startDate && !filters.endDate) return "All Time";
    const format = (value) =>
      value
        ? new Date(value).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : "";
    return `${format(filters.startDate) || "Start"} - ${format(filters.endDate) || "Today"}`;
  }, [filters.startDate, filters.endDate]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const toggleInsight = (key) => {
    setSelectedInsights((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const buildReportUrl = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return `${API_URL}?${params.toString()}`;
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildReportUrl());
      const data = await response.json();
      setReport(data);
    } catch (err) {
      console.error("Failed to generate report:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setReport(null);
  };

  const downloadPdf = () => {
    if (!report) return;

    const timeLabels = {
      all: "All Day",
      morning: "Morning",
      afternoon: "Afternoon",
      evening: "Evening",
      night: "Night",
    };
    const selectedDriver = options.drivers.find((item) => item._id === filters.driver);
    const filterRows = [
      ["Date Range", dateLabel],
      ["Time Period", timeLabels[filters.timePeriod] || "All Day"],
      ["Area / Location", filters.area === "all" ? "All Areas" : filters.area],
      ["Driver", filters.driver === "all" ? "All Drivers" : selectedDriver?.username || "Selected Driver"],
      ["Report Type", "Summary Report"],
    ];

    const escapePdf = (value) =>
      String(value ?? "")
        .replace(/\\/g, "\\\\")
        .replace(/\(/g, "\\(")
        .replace(/\)/g, "\\)");

    const centerX = (text, fontSize) => Math.max(40, (612 - String(text).length * fontSize * 0.5) / 2);
    const ops = [];
    const text = (value, x, y, size = 10, font = "F1", color = "0.12 0.16 0.22") => {
      ops.push("BT");
      ops.push(`${color} rg`);
      ops.push(`/${font} ${size} Tf`);
      ops.push(`${x} ${y} Td`);
      ops.push(`(${escapePdf(value)}) Tj`);
      ops.push("ET");
    };
    const line = (x1, y1, x2, y2, color = "0.88 0.91 0.95") => {
      ops.push(`${color} RG`);
      ops.push("0.8 w");
      ops.push(`${x1} ${y1} m ${x2} ${y2} l S`);
    };
    const box = (x, y, width, height, color = "0.98 0.99 1") => {
      ops.push(`${color} rg`);
      ops.push(`${x} ${y} ${width} ${height} re f`);
    };

    box(0, 785, 612, 57, "0.98 0.99 1");
    ops.push("0.10 0.62 0.25 rg");
    ops.push("48 795 42 32 re f");
    text("WW", 58, 806, 14, "F2", "1 1 1");
    text(
      "WasteWatch - An Intelligent Waste Management System",
      centerX("WasteWatch - An Intelligent Waste Management System", 17),
      812,
      17,
      "F2"
    );
    text("Summary Report", centerX("Summary Report", 14), 790, 14, "F2", "0.75 0.05 0.05");
    line(40, 778, 572, 778, "0.85 0.88 0.92");

    text("Selected Filters", 48, 748, 13, "F2");
    box(48, 638, 516, 94, "0.97 0.98 1");
    filterRows.forEach(([label, value], index) => {
      const row = Math.floor(index / 2);
      const col = index % 2;
      const x = col === 0 ? 66 : 318;
      const y = 708 - row * 30;
      text(label, x, y + 12, 9, "F2", "0.28 0.33 0.41");
      text(value, x, y - 2, 10, "F1", "0.08 0.12 0.18");
    });

    text("Report Summary", 48, 610, 13, "F2");
    const summaryItems = [
      ["Total Complaints", report.summary.totalComplaints],
      ["Completed", report.summary.completedComplaints],
      ["Pending", report.summary.pendingComplaints],
      ["New Users", report.summary.newUsers],
      ["Active Drivers", report.summary.activeDrivers],
    ];
    summaryItems.forEach(([label, value], index) => {
      const x = 48 + index * 103;
      box(x, 548, 92, 45, index % 2 === 0 ? "1 0.96 0.96" : "0.95 0.99 0.97");
      text(label, x + 9, 574, 8, "F2", "0.28 0.33 0.41");
      text(value, x + 9, 556, 18, "F2", "0.05 0.09 0.16");
    });

    const tableHeader = (title, y) => {
      text(title, 48, y, 12, "F2");
      line(48, y - 8, 564, y - 8);
    };
    tableHeader("High Risk Areas", 515);
    const highRiskRows = report.highRiskAreas.slice(0, 6);
    if (highRiskRows.length === 0) {
      text("No area data found for selected filters.", 64, 492, 10);
    } else {
      highRiskRows.forEach((item, index) => {
        const y = 492 - index * 19;
        text(item.name, 64, y, 10);
        text(`${item.count} complaints`, 380, y, 10, "F2");
        text(`${item.percentage}%`, 500, y, 10, "F2", "0.75 0.05 0.05");
      });
    }

    tableHeader("Complaints by Driver", 365);
    const driverRows = report.complaintsByDriver.slice(0, 6);
    if (driverRows.length === 0) {
      text("No driver data found for selected filters.", 64, 342, 10);
    } else {
      driverRows.forEach((item, index) => {
        const y = 342 - index * 19;
        text(item.name, 64, y, 10);
        text(`${item.count} assigned`, 380, y, 10, "F2");
        text(`${item.percentage}%`, 500, y, 10, "F2", "0.75 0.05 0.05");
      });
    }

    tableHeader("Waste Types Breakdown", 215);
    report.wasteTypes.forEach((item, index) => {
      const y = 192 - index * 19;
      text(item.name, 64, y, 10);
      text(`${item.count} complaints`, 380, y, 10, "F2");
      text(`${item.percentage}%`, 500, y, 10, "F2", "0.75 0.05 0.05");
    });

    box(48, 54, 516, 82, "0.96 0.98 1");
    text("Insights", 64, 112, 12, "F2", "0.10 0.32 0.70");
    report.insights.slice(0, 3).forEach((insight, index) => {
      text(`- ${insight}`, 64, 91 - index * 18, 10);
    });
    text(`Generated on ${new Date().toLocaleString()}`, 400, 32, 8, "F1", "0.40 0.46 0.54");

    const content = ops.join("\n");

    const objects = [
      "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
      "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
      "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 842] /Resources << /Font << /F1 4 0 R /F2 6 0 R >> >> /Contents 5 0 R >> endobj",
      "4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
      `5 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`,
      "6 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >> endobj",
    ];

    let pdf = "%PDF-1.4\n";
    const offsets = [0];
    objects.forEach((obj) => {
      offsets.push(pdf.length);
      pdf += `${obj}\n`;
    });
    const xref = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    offsets.slice(1).forEach((offset) => {
      pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
    });
    pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

    const blob = new Blob([pdf], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `WasteWatch_Analytics_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const maxAreaCount = Math.max(...(report?.highRiskAreas || []).map((item) => item.count), 1);
  const maxDriverCount = Math.max(...(report?.complaintsByDriver || []).map((item) => item.count), 1);
  const wasteChartData = report?.wasteTypes || [];
  const wasteColors = ["#8b3df4", "#22c55e", "#6b7280"];

  return (
    <div className="generate-analytics">
      <div className="analytics-header">
        <div>
          <h1><FaChartBar /> Generate Analytics</h1>
          <p>Create custom reports and get insights about waste management activities.</p>
        </div>

        <div className="analytics-actions">
          <button className="reset-btn" onClick={resetFilters}>
            <FaRedo /> Reset Filters
          </button>
          <button className="generate-btn" onClick={generateReport} disabled={loading}>
            <FaFileAlt /> {loading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      <div className="analytics-card criteria-card">
        <div className="criteria-title"><span>1</span> Select Report Criteria</div>

        <div className="filter-grid">
          <label>
            <span><FaCalendarAlt /> Date Range</span>
            <div className="date-range-inputs">
              <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
              <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
            </div>
          </label>

          <label>
            <span><FaClock /> Time Period</span>
            <select name="timePeriod" value={filters.timePeriod} onChange={handleFilterChange}>
              <option value="all">All Day</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
              <option value="night">Night</option>
            </select>
          </label>

          <label>
            <span><FaMapMarkerAlt /> Area / Location</span>
            <select name="area" value={filters.area} onChange={handleFilterChange}>
              <option value="all">All Areas</option>
              {options.areas.map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </label>

          <label>
            <span><FaUser /> Driver</span>
            <select name="driver" value={filters.driver} onChange={handleFilterChange}>
              <option value="all">All Drivers</option>
              {options.drivers.map((driver) => (
                <option key={driver._id} value={driver._id}>{driver.username}</option>
              ))}
            </select>
          </label>

          <label>
            <span><FaFileAlt /> Report Type</span>
            <select name="reportType" value={filters.reportType} onChange={handleFilterChange}>
              <option value="summary">Summary Report</option>
            </select>
          </label>
        </div>

        <div className="criteria-title second"><span>2</span> Choose Insights</div>
        <div className="insight-grid">
          {insightOptions.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`insight-option ${selectedInsights.includes(item.key) ? "selected" : ""}`}
              onClick={() => toggleInsight(item.key)}
            >
              <span className="check-box">{selectedInsights.includes(item.key) ? "✓" : ""}</span>
              <span className="insight-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {report && (
        <div className="generated-report">
          <div className="analytics-card summary-card">
            <h2>Report Summary <span>({dateLabel})</span></h2>
            <div className="summary-grid">
              <div className="summary-tile red"><FaClipboardList /><p>Total Complaints</p><strong>{report.summary.totalComplaints}</strong></div>
              <div className="summary-tile green"><FaCheckSquare /><p>Completed</p><strong>{report.summary.completedComplaints}</strong></div>
              <div className="summary-tile amber"><FaRegClock /><p>Pending</p><strong>{report.summary.pendingComplaints}</strong></div>
              <div className="summary-tile blue"><FaUsers /><p>New Users</p><strong>{report.summary.newUsers}</strong></div>
              <div className="summary-tile purple"><FaTruck /><p>Active Drivers</p><strong>{report.summary.activeDrivers}</strong></div>
            </div>
          </div>

          <div className="analytics-report-grid">
            {selectedInsights.includes("risk") && (
              <div className="analytics-card report-panel">
                <h3><FaMapMarkerAlt /> High Risk Areas</h3>
                <p>Areas with most complaints</p>
                {report.highRiskAreas.slice(0, 4).map((item) => (
                  <div className="metric-row" key={item.name}>
                    <span>{item.name}</span>
                    <div className="metric-track"><div style={{ width: `${(item.count / maxAreaCount) * 100}%` }} /></div>
                    <strong>{item.count} ({item.percentage}%)</strong>
                  </div>
                ))}
              </div>
            )}

            {selectedInsights.includes("overview") && (
              <div className="analytics-card report-panel">
                <h3>Daily Overview</h3>
                <p>Key numbers for selected period</p>
                {report.dailyOverview.map((item) => (
                  <div className="overview-row" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            )}

            {selectedInsights.includes("users") && (
              <div className="analytics-card report-panel">
                <h3><FaUsers /> User Registrations</h3>
                <p>New users registered in selected period</p>
                <div className="registration-highlight">
                  <FaUsers />
                  <div>
                    <span>New Users</span>
                    <strong>{report.summary.newUsers}</strong>
                  </div>
                </div>
              </div>
            )}

            {selectedInsights.includes("drivers") && (
              <div className="analytics-card report-panel">
                <h3>Complaints by Driver</h3>
                <p>Number of complaints assigned</p>
                {report.complaintsByDriver.slice(0, 4).map((item) => (
                  <div className="metric-row" key={item.name}>
                    <span>{item.name}</span>
                    <div className="metric-track"><div style={{ width: `${(item.count / maxDriverCount) * 100}%` }} /></div>
                    <strong>{item.count} ({item.percentage}%)</strong>
                  </div>
                ))}
              </div>
            )}

            {selectedInsights.includes("waste") && (
              <div className="analytics-card report-panel waste-panel">
                <h3>Waste Types Breakdown</h3>
                <p>Distribution by waste category</p>
                <div className="waste-chart-wrap">
                  <ResponsiveContainer width="48%" height={170}>
                    <PieChart>
                      <Pie data={wasteChartData} dataKey="percentage" innerRadius={45} outerRadius={72}>
                        {wasteChartData.map((entry, index) => (
                          <Cell key={entry.name} fill={wasteColors[index % wasteColors.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="waste-legend">
                    {wasteChartData.map((item, index) => (
                      <div key={item.name}><span style={{ background: wasteColors[index % wasteColors.length] }} /> {item.name} <strong>{item.percentage}%</strong></div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="analytics-card insights-box">
            <div>
              <h3><FaLightbulb /> Insights</h3>
              {report.insights.map((insight) => <p key={insight}>{insight}</p>)}
            </div>
            <button className="pdf-btn" onClick={downloadPdf}>
              <FaFilePdf /> Export Report (PDF)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateAnalytics;
